import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTokenFromQuery } from '../utils/auth.utils';
import { fetchData } from '../../../app/api/utils/API.utils';
import { Endpoints } from '../../../app/api/model/API.endpoints';
import { VerifyTokenResponseDto } from '../model/auth.model';
import {
  setStoredUserId,
  clearStoredUserId,
  validateAndClearCacheIfUserChanged,
  clearPersistedQueryCache,
} from '../../../utils/localStorage.utils';

type AuthContextValue = {
  token: string | null;
  isAuthenticated: boolean;
  ready: boolean;
  logout: () => void;
  userId?: number;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: tokenVerifyResponse,
    isLoading,
    isSuccess: isSuccessVerify,
    isError: isErrorTokenVerify,
  } = useQuery({
    queryKey: ['jwks'],
    enabled: Boolean(token),
    retry: false,
    cacheTime: 0,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    meta: {
      persist: false,
    },
    queryFn: () => fetchData<VerifyTokenResponseDto>(Endpoints.AUTH_VERIFY, {}),
  });

  const logout = useCallback(() => {
    setToken(null);
    queryClient.clear();
    clearStoredUserId();
    clearPersistedQueryCache();
  }, [queryClient]);

  useEffect(() => {
    const queryToken = getTokenFromQuery();
    const initialToken = queryToken ?? null;

    if (initialToken) {
      setToken(initialToken);
      setReady(false);
    } else {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    if (!token || isLoading) {
      return;
    }

    const checkTokenVerifyResponse = async () => {
      if (isErrorTokenVerify || !tokenVerifyResponse?.userId) {
        logout();
      }
      setReady(true);
    };

    checkTokenVerifyResponse();
  }, [isErrorTokenVerify, isLoading, logout, token, tokenVerifyResponse?.userId]);

  useEffect(() => {
    const currentUserId = tokenVerifyResponse?.userId;
    
    if (!currentUserId || !isSuccessVerify) {
      return;
    }

    validateAndClearCacheIfUserChanged(currentUserId);

    setStoredUserId(currentUserId);
  }, [tokenVerifyResponse?.userId, isSuccessVerify]);

  const value = useMemo(
    () => ({
      token,
      isAuthenticated: Boolean(token) && isSuccessVerify && ready,
      ready,
      logout,
      userId: tokenVerifyResponse?.userId,
    }),
    [isSuccessVerify, logout, ready, token, tokenVerifyResponse?.userId]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
